/** Mock Brain agent — canned admin intelligence until a real tool-calling LLM is wired. */

export type BrainRole = 'user' | 'assistant' | 'system';

export type BrainBlock =
  | { type: 'text'; text: string }
  | { type: 'metric'; label: string; value: string; hint?: string }
  | { type: 'table'; columns: string[]; rows: string[][] }
  | { type: 'links'; items: { label: string; href: string }[] }
  | { type: 'tool'; name: string; detail: string };

export interface BrainMessage {
  id: string;
  role: BrainRole;
  createdAt: number;
  blocks: BrainBlock[];
}

export const BRAIN_SUGGESTIONS = [
  'How many published CMS pages do we have?',
  'Summarize listing queues that need review',
  'Show recent payment failures',
  'Draft a status update for ops',
  'Where do I manage tags and menus?',
];

function uid() {
  return `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function match(q: string, ...needles: string[]) {
  const s = q.toLowerCase();
  return needles.some((n) => s.includes(n));
}

/** Produce a mock assistant reply for an admin question. */
export function mockBrainReply(question: string): BrainMessage {
  const q = question.trim();

  if (match(q, 'cms', 'content', 'page', 'pages')) {
    return {
      id: uid(),
      role: 'assistant',
      createdAt: Date.now(),
      blocks: [
        { type: 'tool', name: 'cms.list', detail: 'Counted cms_object rows (mock staging snapshot)' },
        { type: 'text', text: 'Here’s a mock read of CMS pages on staging:' },
        { type: 'metric', label: 'Published', value: '128', hint: 'live on public site' },
        { type: 'metric', label: 'Drafts', value: '47', hint: 'not yet published' },
        { type: 'metric', label: 'Inactive / expired', value: '12' },
        {
          type: 'table',
          columns: ['Title', 'Type', 'Status', 'Updated'],
          rows: [
            ['Delivery zones — Ghana & Nigeria', 'page', 'published', '5 May 2026'],
            ['Help · Escrow basics', 'help', 'published', '2 May 2026'],
            ['Privacy policy refresh', 'legal', 'draft', '28 Apr 2026'],
          ],
        },
        {
          type: 'links',
          items: [
            { label: 'Open CMS pages', href: '/admin/content/pages' },
            { label: 'Media library', href: '/admin/content/media' },
          ],
        },
      ],
    };
  }

  if (match(q, 'listing', 'queue', 'review', 'moderation')) {
    return {
      id: uid(),
      role: 'assistant',
      createdAt: Date.now(),
      blocks: [
        { type: 'tool', name: 'listings.queues', detail: 'Mock queue depths from listing management' },
        { type: 'text', text: 'Listing review queues that need attention right now (mock):' },
        { type: 'metric', label: 'Pending review', value: '34' },
        { type: 'metric', label: 'Flagged', value: '9' },
        { type: 'metric', label: 'Rejected (7d)', value: '21' },
        {
          type: 'table',
          columns: ['Listing', 'Reason', 'Age'],
          rows: [
            ['Accra 2BR · Airport Hills', 'Missing title deed scan', '14h'],
            ['Lagos SUV · Ikeja', 'Price outlier vs comps', '1d'],
            ['Cape Coast guest house', 'Duplicate of #88421', '2d'],
          ],
        },
        {
          type: 'links',
          items: [{ label: 'Open listing management', href: '/admin/listings' }],
        },
      ],
    };
  }

  if (match(q, 'payment', 'refund', 'invoice', 'wallet', 'fail')) {
    return {
      id: uid(),
      role: 'assistant',
      createdAt: Date.now(),
      blocks: [
        { type: 'tool', name: 'payments.failures', detail: 'Mock last-24h payment error rollup' },
        { type: 'text', text: 'Payment health snapshot (mock, last 24 hours):' },
        { type: 'metric', label: 'Failed charges', value: '18', hint: '↑ 4 vs prior day' },
        { type: 'metric', label: 'Open refunds', value: '6' },
        { type: 'metric', label: 'Success rate', value: '97.2%' },
        {
          type: 'table',
          columns: ['Code', 'Count', 'Note'],
          rows: [
            ['card_declined', '11', 'Mostly GTBank BIN'],
            ['insufficient_funds', '5', 'Retryable'],
            ['processor_timeout', '2', 'Paystack blip 03:12 UTC'],
          ],
        },
        {
          type: 'links',
          items: [
            { label: 'Payments', href: '/admin/payments' },
            { label: 'Refund requests', href: '/admin/payments/refunds' },
          ],
        },
      ],
    };
  }

  if (match(q, 'tag', 'menu', 'navigation', 'where')) {
    return {
      id: uid(),
      role: 'assistant',
      createdAt: Date.now(),
      blocks: [
        {
          type: 'text',
          text: 'Quick map of where those live in admin:',
        },
        {
          type: 'links',
          items: [
            { label: 'Tags catalog', href: '/admin/content/tags' },
            { label: 'Site navigation / menus', href: '/admin/navigation' },
            { label: 'CMS pages', href: '/admin/content/pages' },
            { label: 'Feature flags', href: '/admin/features/flags' },
          ],
        },
        {
          type: 'text',
          text: 'Tip: open a page in CMS → History & slugs / Menu links in the row actions for per-page wiring.',
        },
      ],
    };
  }

  if (match(q, 'draft', 'status update', 'ops', 'write', 'generate', 'summary')) {
    return {
      id: uid(),
      role: 'assistant',
      createdAt: Date.now(),
      blocks: [
        { type: 'tool', name: 'compose.status', detail: 'Generated from mock KPIs across CMS, listings, payments' },
        {
          type: 'text',
          text:
            '**Ops status (draft — mock data)**\n\n' +
            '• CMS: 128 published / 47 drafts; no publish failures in the last 24h.\n' +
            '• Listings: 34 awaiting review, 9 flagged — oldest item 2 days.\n' +
            '• Payments: 97.2% success; 18 failures concentrated on card_declined.\n' +
            '• Ask: prioritize listing queue clearance before Friday promo push.\n\n' +
            '_This is mock copy. Wire Brain to live admin APIs to refresh numbers._',
        },
        {
          type: 'links',
          items: [
            { label: 'Dashboard', href: '/admin' },
            { label: 'Campaigns', href: '/admin/campaigns' },
          ],
        },
      ],
    };
  }

  if (match(q, 'user', 'users', 'waitlist', 'permission', 'role')) {
    return {
      id: uid(),
      role: 'assistant',
      createdAt: Date.now(),
      blocks: [
        { type: 'tool', name: 'users.snapshot', detail: 'Mock users & permissions rollup' },
        { type: 'metric', label: 'Active users (7d)', value: '4,812' },
        { type: 'metric', label: 'Waitlist', value: '226' },
        { type: 'metric', label: 'Admin roles', value: '14' },
        {
          type: 'links',
          items: [
            { label: 'Users', href: '/admin/users' },
            { label: 'Roles', href: '/admin/roles' },
            { label: 'Waitlist', href: '/admin/users/waitlist' },
          ],
        },
      ],
    };
  }

  return {
    id: uid(),
    role: 'assistant',
    createdAt: Date.now(),
    blocks: [
      {
        type: 'text',
        text:
          'I’m **Brain** — a mock admin co-pilot. I can pretend to check CMS, listings, payments, users, and draft ops notes.\n\n' +
          'Try asking about pages, listing queues, payment failures, or say “draft a status update”. Live tool calling isn’t wired yet; replies use staging-shaped fixtures.',
      },
      {
        type: 'links',
        items: [
          { label: 'CMS pages', href: '/admin/content/pages' },
          { label: 'Listings', href: '/admin/listings' },
          { label: 'Payments', href: '/admin/payments' },
          { label: 'LLM model controls', href: '/admin/ai/controls' },
        ],
      },
    ],
  };
}

export function makeUserMessage(text: string): BrainMessage {
  return {
    id: uid(),
    role: 'user',
    createdAt: Date.now(),
    blocks: [{ type: 'text', text }],
  };
}

export function welcomeMessage(): BrainMessage {
  return mockBrainReply('help');
}
